// src/pages/Success.tsx
// Post-payment success page
// SECURED: Verifies Stripe session_id before granting access
// Sends welcome WhatsApp message, then redirects to dashboard

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSupabase, currentUser } from "@/lib/supabase";
import { ENV } from "@/lib/env";

export default function Success() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "verifying" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyAndComplete = async () => {
      try {
        const supabase = getSupabase();
        const user = await currentUser();
        
        if (!user) {
          navigate("/signup", { replace: true });
          return;
        }

        // Get session_id from URL (Stripe adds this)
        const sessionId = searchParams.get("session_id");
        
        if (!sessionId) {
          // No session_id = someone navigated here directly
          // Check if they already have an active subscription
          const { data: client } = await supabase
            .from("clients")
            .select("subscription_status")
            .eq("user_id", user.id)
            .single();

          const validStatuses = ["active", "trialing", "trial"];
          if (client && validStatuses.includes(client.subscription_status?.toLowerCase())) {
            // Already paid, redirect to dashboard
            navigate("/dashboard", { replace: true });
            return;
          }

          // Not paid, redirect to payment
          setErrorMessage("Payment not verified. Please complete checkout.");
          setStatus("error");
          setTimeout(() => navigate("/payment", { replace: true }), 2000);
          return;
        }

        // Verify the session with Stripe via edge function
        setStatus("verifying");
        
        const verifyRes = await fetch(`${ENV.EDGE_BASE}/verify-checkout-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ 
            session_id: sessionId,
            user_id: user.id 
          }),
        });

        const verifyData = await verifyRes.json();

        if (!verifyRes.ok || !verifyData.verified) {
          throw new Error(verifyData.error || "Payment verification failed");
        }

        // Payment verified! Now complete onboarding
        setStatus("success");

        // Get phone number for welcome message
        const { data: client } = await supabase
          .from("clients")
          .select("phone")
          .eq("user_id", user.id)
          .single();

        // Send welcome WhatsApp message
        if (client?.phone) {
          try {
            await fetch(`${ENV.EDGE_BASE}/send-welcome-after-verification`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ phone: client.phone }),
            });
            console.log("✅ Welcome message sent");
          } catch (e) {
            console.error("Failed to send welcome message:", e);
          }
        }

        // Redirect to dashboard after brief delay
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 2500);

      } catch (err: any) {
        console.error("Error in success page:", err);
        setErrorMessage(err.message || "Something went wrong");
        setStatus("error");
        setTimeout(() => {
          navigate("/payment", { replace: true });
        }, 3000);
      }
    };

    verifyAndComplete();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-center px-6 py-4 border-b border-[#e2e8f0]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center px-4 min-h-[calc(100vh-130px)]">
        <div className="w-full max-w-md text-center">
          
          {(status === "loading" || status === "verifying") && (
            <div className="animate-in fade-in duration-300">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full border-4 border-[#a78bfa] border-t-transparent animate-spin" />
              <p className="text-gray-500">
                {status === "verifying" ? "Verifying payment..." : "Loading..."}
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-in fade-in zoom-in duration-500">
              {/* Success Icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#a78bfa] flex items-center justify-center">
                <svg className="w-10 h-10 text-[#0f172a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Text */}
              <h1 className="text-3xl font-bold text-[#0f172a] mb-3">
                You're all set!
              </h1>
              <p className="text-gray-500 mb-8">
                Welcome to Input
              </p>

              {/* Animated dots */}
              <div className="flex items-center justify-center gap-1 mb-8">
                <span className="text-gray-500">Taking you to your dashboard</span>
                <span className="flex gap-1 ml-1">
                  <span className="w-1.5 h-1.5 bg-[#a78bfa] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-[#a78bfa] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-[#a78bfa] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              </div>

              {/* Manual button */}
              <button
                onClick={() => navigate("/dashboard", { replace: true })}
                className="text-[#a78bfa] hover:text-[#0f172a] transition-colors text-sm font-medium"
              >
                Go to dashboard →
              </button>
            </div>
          )}

          {status === "error" && (
            <div className="animate-in fade-in duration-300">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-[#0f172a] mb-2">
                Payment Not Verified
              </h1>
              <p className="text-gray-500 mb-6">
                {errorMessage || "Redirecting to checkout..."}
              </p>
              <button
                onClick={() => navigate("/payment", { replace: true })}
                className="px-6 py-3 bg-[#a78bfa] text-[#0f172a] font-semibold rounded-xl"
              >
                Go to checkout
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>
    </div>
  );
}
